import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { forwardRef } from 'react';

import DeleteIcon from '@/assets/all-icons/delete-icon';
import useTranslations from '@/i18n/useTranslations';
import HandleDragListIcon from '@/shared-components/icons/components/forms/HandleDragListIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';

/**
 * Sortable tier row used inside the support setup form.
 * The component forwards the ref so that dnd-kit can control the DOM node.
 */
const SupportTierRow = forwardRef(function SupportTierRow({
  tier,
  onChange,
  onRemove,
  disableRemove,
  currencySymbol = '€'
}, ref) {
  const t = useTranslations('common.profile.buttons.support.setup.tier_row');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tier.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={node => {
        setNodeRef(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={`support-setup__tier ${isDragging ? 'support-setup__tier--dragging' : ''} ${disableRemove ? 'support-setup__tier--remove-disabled' : ''}`}
      style={style}
    >
      <div className="support-setup__tier-drag" {...attributes} {...listeners}>
        <HandleDragListIcon className="support-setup__tier-drag-icon" />
      </div>

      <div className="support-setup__tier-fields">
        <PublicFormInput
          placeholder={t('fields.description')}
          value={tier.label}
          onChange={event => onChange({ ...tier, label: event.target.value })}
        />
        <PublicFormInput
          placeholder={t('fields.amount', { currencySymbol })}
          type="number"
          min={1}
          value={tier.amount}
          onChange={event => {
            const { value } = event.target;
            onChange({ ...tier, amount: value === '' ? '' : Number(value) });
          }}
        />
      </div>

      <button
        type="button"
        className="support-setup__tier-remove"
        onClick={() => onRemove(tier.id)}
        disabled={disableRemove}
        aria-label={t('actions.remove_tier')}
        title={t('actions.remove_tier')}
      >
        <DeleteIcon className="support-setup__tier-remove-icon" />
      </button>
    </div>
  );
});

export default SupportTierRow;
